import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom"; // Importar useNavigate, useSearchParams y useLocation
import { FiFilter } from "react-icons/fi";
import Badge from "../../components/badge/badge";
import { getApiInstance } from "../../utils/api";
import { LuEye } from "react-icons/lu";
import InputSearch from "../../components/inputs/inputSearch/inputSearch";
import InputDropdown from "../../components/inputs/inputDropdown/inputDropdown";
import Button from "../../components/button/button";
import Modal from "../../components/modal/modal";
import Accordion from "../../components/accordion/accordion";
import Tag from "../../components/tag/tag";
import Table from "../../components/table/table";
import SectionHeader from "../../components/sectionHeader/sectionHeader";
import { BiExport } from "react-icons/bi";
import { matchesSearchInObject } from "../../utils/searchUtils";

const columnsSinEstado = [
    {
        key: "rutaImagen",
        label: "Miniatura",
        render: (row) => (
            <img
                src={row.rutaImagen}
                alt={row.nombreBook}
                style={{ width: "40px", height: "40px" }}
            />
        ),
    },
    {
        key: "tipoCambio",
        label: "Tipo de Cambio",
        render: (row) => {
            let variant;

            switch (row.tipoCambio) {
                case "Nueva familia":
                    variant = "success";
                    break;
                case "Familia modificada":
                    variant = "warning";
                    break;
                case "Familia eliminada":
                    variant = "danger";
                    break;
                default:
                    variant = "default";
            }

            return <Badge label={row.tipoCambio || "N/A"} variant={variant} />;
        },
    },
    { key: "nombreBook", label: "Nombre" },
    { key: "referencia", label: "Referencia" },
    { key: "medida", label: "Unidad de medida" },
    { key: "nombreFamilia", label: "Familia" },
    {
        key: "variante",
        label: "Variante",
        render: (row) => row.variante || "N/A",
    },
    {
        key: "nombreFamilia",
        label: "Categoría",
        render: (row) => familiaMapping[row.codFamilia] || "N/A",
    },
];

const familiaMapping = {
    1: "Mobiliario Central",
    2: "Mobiliario Perimetral",
    3: "Mesas",
    4: "Probadores",
    5: "Caja",
    6: "Dotación",
    7: "Instalaciones",
    8: "Señalética",
    9: "BOH",
    10: "Dotaciones fijas por tienda",
};

const sidebarCategoriasOptions = [
    "Mobiliario Central",
    "Mobiliario Perimetral",
    "Mesas",
    "Probadores",
    "Caja",
    "Dotación",
    "Instalaciones",
    "Señalética",
    "BOH",
    "Dotaciones fijas por tienda",
];

const categoriasImagenMap = {
    8: "MAKEUP400",
    9: "MUNICH",
    10: "COPENHAGUE",
    11: "MIAMI-SPLAU",
    13: "OXFORD",
};

const imagenesOptions = ["MUNICH", "COPENHAGUE", "MIAMI-SPLAU", "MAKEUP400", "OXFORD"];

export default function Versions() {
    const navigate = useNavigate(); // Hook de navegación
    const [searchTerm, setSearchTerm] = useState("");
    const [versionOptions, setVersionOptions] = useState([]);
    const [selectedVersion, setSelectedVersion] = useState("");
    const [selectedVersionValue, setSelectedVersionValue] = useState("");
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const [mueblesPorVersion, setMueblesPorVersion] = useState([]);
    const [sortOrder, setSortOrder] = useState(null);
    const [filterCategoria, setFilterCategorias] = useState([]);
    const [filterImagen, setFilterImagen] = useState([]);
    const [subCategorias, setSubcategorias] = useState([]);
    const [categorias, setCategorias] = useState(sidebarCategoriasOptions);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [filterSubcategoria, setFilterSubcategoria] = useState([]);

    const api = getApiInstance();

    const fetchVersiones = async () => {
        try {
            const response = await api.get("/api/Book/GetAllBooks");

            // Ordenar por fecha de publicación (más reciente primero)
            const booksOrdered = response.data
                .slice()
                .sort(
                    (a, b) => new Date(b.publicado).getTime() - new Date(a.publicado).getTime()
                );

            const versions = booksOrdered.map((book) => ({
                label: `Versión v.${book.version} - Publicado el: ${new Date(
                    book.publicado
                ).toLocaleDateString()}`,
                value: book.codBook,
            }));

            setVersionOptions(versions);
            // Si hay ?version=codBook en la URL, seleccionar esa versión automáticamente
            const versionParam = searchParams.get("version");
            if (versionParam) {
                // comparar como string para evitar mismatches number/string
                const matched = versions.find(
                    (v) => String(v.value) === String(versionParam)
                );
                if (matched) {
                    setSelectedVersion(matched.label);
                    setSelectedVersionValue(matched.value);
                    fetchMueblesPorVersion(matched.value);
                }
            }
        } catch (error) {
            console.error("Error al obtener las versiones", error);
        }
    };

    const fetchMueblesPorVersion = async (codBook) => {
        try {
            const response = await api.get(
                `/api/Mueble/GetMueblesPorVersion/${codBook}`
            );
            setMueblesPorVersion(response.data);
        } catch (error) {
            console.error(
                "Error al obtener los muebles con cambios por versión",
                error
            );
            setMueblesPorVersion([]);
        }
    };

    const handleVersionSelect = (option) => {
        const selected = versionOptions.find((v) => v.value === option.value);
        if (selected) {
            setSelectedVersion(selected.label);
            setSelectedVersionValue(option.value);
            // Guardar en la URL para poder restaurarlo al volver atrás
            setSearchParams({ version: option.value });
            fetchMueblesPorVersion(option.value);
        }
    };

    // Función para navegar a la ficha del mueble
    const handleVerCambios = (row) => {
        // preservamos los query params actuales al navegar a la ficha
        const search = location.search || "";
        navigate(`/ficha-producto/${row.codMueble}${search}`);
    };

    useEffect(() => {
        fetchVersiones();
    }, []);

    useEffect(() => {
        console.log(mueblesPorVersion);
        const allSubcategorias = mueblesPorVersion
            .map((product) => product.categorias)
            .flat()
            .filter(
                (value, index, self) =>
                    self.indexOf(value) === index && value != null
            );

        setSubcategorias(allSubcategorias);
        setCategorias(sidebarCategoriasOptions);
    }, [mueblesPorVersion]);

    // Filtrar y ordenar muebles
    const filteredMuebles = useMemo(() => {
        let filtered = mueblesPorVersion.filter((mueble) =>
            matchesSearchInObject(mueble, searchTerm)
        );

        if (sortOrder === "A-Z") {
            return filtered.sort((a, b) =>
                a.nombreBook.localeCompare(b.nombreBook)
            );
        } else if (sortOrder === "Z-A") {
            return filtered.sort((a, b) =>
                b.nombreBook.localeCompare(a.nombreBook)
            );
        }

        return filtered
            .filter(
                (product) =>
                    filterCategoria.length === 0 ||
                    filterCategoria.includes(familiaMapping[product.codFamilia])
            )
            .filter(
                (product) => {
                    if (filterImagen.length === 0) return true;

                    const productImagenes =
                        product.codCategorias?.
                            map((codigo) => categoriasImagenMap[codigo])
                            .filter(Boolean) || [];

                    return productImagenes.some((imagen) =>
                        filterImagen.includes(imagen)
                    );
                }
            );
    }, [
        searchTerm,
        mueblesPorVersion,
        sortOrder,
        filterCategoria,
        filterImagen,
    ]);

    const antionsVersiones = (row) => [
        {
            label: "Ver detalle",
            onClick: () => handleVerCambios(row),
            icon: <LuEye />,
        },
    ];



    const openModal = () => {
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    const handleAcceptFilters = () => {
        closeModal();
    };

    const handleCleanFilter = () => {
        setFilterCategorias([]);
        setFilterSubcategoria([]);
        setFilterImagen([]);
    };



    const handleSelectCategoria = (categoriaName) => {
        setFilterCategorias((prevSelected) => {
            if (prevSelected.includes(categoriaName)) {
                return prevSelected.filter(
                    (categoria) => categoria !== categoriaName
                );
            } else {
                return [...prevSelected, categoriaName];
            }
        });
    };

    const handleSelectImagen = (imagenName) => {
        setFilterImagen((prevSelected) => {
            if (prevSelected.includes(imagenName)) {
                return prevSelected.filter(
                    (imagen) => imagen !== imagenName
                );
            } else {
                return [...prevSelected, imagenName];
            }
        });
    };

    const handleDownloadPlanosYExcelZip = async () => {
        if (mueblesPorVersion.length === 0) {
            alert(
                "Selecciona al menos un mueble para exportar los planos y el Excel."
            );
            return;
        }

        try {
            const response = await api.post(
                "/api/Mueble/DownloadPlanosYExcelZip",
                mueblesPorVersion.map((mueble) => mueble.codMueble),
                {
                    responseType: "blob",
                }
            );

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "PlanosYExcelMuebles.zip");
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error("Error al exportar los planos y Excel:", error);
            alert("Hubo un error al intentar exportar los planos y el Excel.");
        }
    };

    return (
        <>
            <SectionHeader
                title="Versiones publicadas"
                subtitle="Lista de versiones publicadas en el Book"
            />

            <div className="dashboard-productos__content__table-header">
                <InputSearch
                    context="tableSearch"
                    placeholder="Buscar..."
                    onSearch={setSearchTerm}
                />
                    <InputDropdown
                        options={versionOptions}
                        onSelect={handleVersionSelect}
                        placeholder="Selecciona una versión"
                        showLabel={false}
                        value={{ label: selectedVersion }}
                    />
                <div className="dashboard-productos__content__table-header__buttons">
                    <Button
                        label="Filtros"
                        onClick={openModal}
                        icon={<FiFilter />}
                    />
                    <Button
                        label="Exportar"
                        variant="outline"
                        icon={<BiExport />}
                        onClick={handleDownloadPlanosYExcelZip}
                        disabled={mueblesPorVersion.length === 0}
                    />
                </div>
            </div>
            <Table
                columns={columnsSinEstado}
                data={filteredMuebles || []}
                actions={antionsVersiones}
                noResultsMessage="No se han encontrado muebles para la versión seleccionada."
            />

            <Modal
                title="Filtros"
                isOpen={isModalOpen}
                onClose={closeModal}
                onAccept={handleAcceptFilters}
                outlineButtonLabel="Limpiar"
                primaryButtonLabel={`Ver resultados (${filteredMuebles.length})`}
                onCancel={handleCleanFilter}
            >
                <div className="section-header__modal__section">
                    <Accordion title="Imagen">
                        <ul className="section-header__modal__section__content">
                            {imagenesOptions.map((imagen, index) => (
                                <Tag
                                    key={index}
                                    label={imagen}
                                    isSelected={filterImagen.includes(
                                        imagen
                                    )}
                                    onSelect={handleSelectImagen}
                                />
                            ))}
                        </ul>
                    </Accordion>
                </div>
                <div className="section-header__modal__section">
                    <Accordion title="Categoría">
                        <ul className="section-header__modal__section__content">
                            {categorias.map((categoria, index) => (
                                <Tag
                                    key={index}
                                    label={categoria}
                                    isSelected={filterCategoria.includes(
                                        categoria
                                    )}
                                    onSelect={handleSelectCategoria}
                                />
                            ))}
                        </ul>
                    </Accordion>
                </div>
            </Modal>
        </>
    );
}
